import React from "react";
import { Cookie, Settings, X } from "lucide-react";

const STORAGE_KEY = "ema_cookie_consent_v1";

/**
 * GDPR cookie consent banner.
 * Categories:
 *  - necessary: always true (booking session, JWT auth, CSRF) — cannot be disabled
 *  - analytics: PostHog product analytics (anonymous usage)
 *  - session_recording: PostHog session replays (only if analytics ON)
 *  - marketing: future ad-platforms (currently unused — kept for future-proofing)
 */
function readConsent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

function writeConsent(state) {
  const payload = {
    ...state,
    ts: new Date().toISOString(),
    version: 1,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  // Apply immediately for PostHog
  try {
    if (window.posthog) {
      if (state.analytics) {
        window.posthog.opt_in_capturing();
        if (state.session_recording && window.posthog.startSessionRecording) {
          window.posthog.startSessionRecording();
        } else if (window.posthog.stopSessionRecording) {
          window.posthog.stopSessionRecording();
        }
      } else {
        window.posthog.opt_out_capturing();
        if (window.posthog.stopSessionRecording) {
          window.posthog.stopSessionRecording();
        }
      }
    }
  } catch {
    /* ignore */
  }
  // Apply for Google Analytics 4 + Facebook Pixel (lazy-loaded on first consent)
  try {
    if (state.analytics && typeof window.__emaLoadGA4 === "function") {
      window.__emaLoadGA4();
    } else if (!state.analytics && typeof window.gtag === "function") {
      window.gtag('consent', 'update', { 'analytics_storage': 'denied' });
    }
    if (state.marketing && typeof window.__emaLoadFBPixel === "function") {
      window.__emaLoadFBPixel();
    } else if (!state.marketing && window.fbq) {
      window.fbq('consent', 'revoke');
    }
  } catch {
    /* ignore */
  }
  // Notify any other listeners
  try {
    window.dispatchEvent(new CustomEvent("ema-consent-updated", { detail: payload }));
  } catch {
    /* ignore */
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const [prefs, setPrefs] = React.useState({
    necessary: true,
    analytics: false,
    session_recording: false,
    marketing: false,
  });

  React.useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      // Defer slightly so it doesn't flash on the first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Listen for external "open" event (footer link)
  React.useEffect(() => {
    const handler = () => {
      const existing = readConsent();
      if (existing) {
        setPrefs({
          necessary: true,
          analytics: !!existing.analytics,
          session_recording: !!existing.session_recording,
          marketing: !!existing.marketing,
        });
      }
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("ema-open-cookie-settings", handler);
    return () => window.removeEventListener("ema-open-cookie-settings", handler);
  }, []);

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true, session_recording: true, marketing: true });
    setVisible(false);
  };

  const rejectAll = () => {
    writeConsent({ necessary: true, analytics: false, session_recording: false, marketing: false });
    setVisible(false);
  };

  const saveCustom = () => {
    writeConsent(prefs);
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  // Skjul cookie-banneren på interne admin-/driver-/subcontractor-ruter — der vises
  // ingen offentligt sporet indhold, og banneren overlapper modaler i dashboardet.
  if (typeof window !== "undefined") {
    const p = window.location.pathname || "";
    if (p.startsWith("/admin") || p.startsWith("/driver") || p.startsWith("/subcontractor") || p.startsWith("/vognmand") || p.startsWith("/business") || p.startsWith("/track") || p.startsWith("/underskriv") || p.startsWith("/upload-dokumenter") || p.startsWith("/b2b-underskriv")) {
      return null;
    }
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] p-3 sm:p-5"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-5xl mx-auto bg-brand-dark text-white rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
        {!showSettings ? (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-3">
              <Cookie size={22} className="text-brand-red shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-lg font-bold mb-1">Vi bruger cookies</h3>
                <p className="text-sm text-white/80 leading-relaxed">
                  Vi bruger nødvendige cookies for at sitet fungerer (login, betaling, kurv).
                  Med dit samtykke bruger vi også anonyme analytics-cookies fra PostHog, der hjælper
                  os med at forbedre EMA Kurer. Læs mere i vores{" "}
                  <a href="/handelsbetingelser#cookies" className="underline hover:text-brand-red">
                    privatlivspolitik
                  </a>
                  .
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={() => setShowSettings(true)}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/30 px-4 py-2.5 text-sm font-semibold hover:bg-white/10 transition-colors"
                data-testid="cookie-customize-btn"
              >
                <Settings size={14} /> Tilpas
              </button>
              <button
                onClick={rejectAll}
                className="rounded-lg border border-white/30 px-4 py-2.5 text-sm font-semibold hover:bg-white/10 transition-colors"
                data-testid="cookie-reject-btn"
              >
                Afvis alle
              </button>
              <button
                onClick={acceptAll}
                className="rounded-lg bg-brand-red text-white px-5 py-2.5 text-sm font-bold hover:bg-brand-red/90 transition-colors"
                data-testid="cookie-accept-btn"
              >
                Acceptér alle
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6" data-testid="cookie-settings-panel">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-lg font-bold">Vælg dine cookies</h3>
                <p className="text-xs text-white/60 mt-0.5">Du kan altid ændre dit valg via footer-linket "Cookies".</p>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="text-white/60 hover:text-white p-1"
                aria-label="Luk"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <CookieRow
                title="Nødvendige cookies"
                desc="Login, JWT-auth, kurv, sikkerhed. Kan ikke deaktiveres."
                checked={true}
                disabled={true}
                onChange={() => {}}
                testid="cookie-row-necessary"
              />
              <CookieRow
                title="Analytics (PostHog)"
                desc="Anonym brugsstatistik som hjælper os med at forbedre flow + opdage fejl."
                checked={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v, session_recording: v ? prefs.session_recording : false })}
                testid="cookie-row-analytics"
              />
              <CookieRow
                title="Session replays"
                desc="Anonyme optagelser af brugerflow (kun aktiv hvis Analytics er valgt). Hjælper med at debug'e UX-problemer."
                checked={prefs.session_recording}
                disabled={!prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, session_recording: v })}
                testid="cookie-row-replay"
              />
              <CookieRow
                title="Marketing (ingen aktive endnu)"
                desc="Vi bruger ikke marketing-cookies i dag, men beder om samtykke nu så vi ikke skal vise banneret igen senere."
                checked={prefs.marketing}
                onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                testid="cookie-row-marketing"
              />
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={rejectAll}
                className="rounded-lg border border-white/30 px-4 py-2.5 text-sm font-semibold hover:bg-white/10 transition-colors"
                data-testid="cookie-reject-from-settings"
              >
                Afvis alle
              </button>
              <button
                onClick={saveCustom}
                className="rounded-lg bg-brand-red text-white px-5 py-2.5 text-sm font-bold hover:bg-brand-red/90 transition-colors"
                data-testid="cookie-save-custom"
              >
                Gem mine valg
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CookieRow({ title, desc, checked, disabled, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10" data-testid={testid}>
      <div className="flex-1">
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-xs text-white/60 mt-0.5">{desc}</div>
      </div>
      <button
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={
          "relative shrink-0 h-6 w-11 rounded-full transition-colors " +
          (checked ? "bg-brand-red" : "bg-white/20") +
          (disabled ? " opacity-50 cursor-not-allowed" : " cursor-pointer")
        }
        aria-pressed={checked}
        aria-label={`Toggle ${title}`}
        data-testid={`${testid}-toggle`}
      >
        <span
          className={
            "absolute top-0.5 h-5 w-5 bg-white rounded-full shadow transition-all " +
            (checked ? "left-[22px]" : "left-0.5")
          }
        />
      </button>
    </div>
  );
}
